import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { AssessmentResult } from '@shared/models';
import { catchError, distinctUntilChanged, finalize, map, tap } from 'rxjs/operators';

@Injectable()
export class AssessmentService {
  private readonly creatingByCourse = new Map<string, BehaviorSubject<Set<string>>>();
  private readonly createdByCourse = new Map<string, Subject<void>>();

  constructor(private _http: HttpClient) { }

  public isCreating$(courseId: string): Observable<boolean> {
    return this.getCreatingSubscription(courseId).asObservable().pipe(
      map(set => set.size > 0),
      distinctUntilChanged()
    );
  }

  isCreating(courseId: string): boolean {
    return this.getCreatingSubscription(courseId).value.size > 0;
  }

  public created$(courseId: string): Observable<void> {
    return this.getCreatedSubscription(courseId).asObservable();
  }

  public getAssessments(courseId: string, limit: number = 10, userName?: string, userType?: string): Observable<AssessmentResult[]> {
    let url = `/api/assessments?courseId=${courseId}&limit=${limit}`;
    if (userName) {
      url += `&userName=${userName}`;
    }
    if (userType) {
      url += `&userType=${userType}`;
    }
    return this._http.get<AssessmentResult[]>(environment.coursesApiUrl + url);
  };

  public getAssessmentBySessionId(sessionId: string): Observable<AssessmentResult | undefined> {
    return this._http.get<any>(environment.coursesApiUrl + `/api/assessments/sessions/${sessionId}`);
  };

  public generate(body: any): Observable<any> {
    const { courseId } = body;
    const tempId = `temp-${crypto.randomUUID()}`;
    const creating$ = this.getCreatingSubscription(courseId);
    creating$.next(new Set([...creating$.value, tempId]));

    return this._http.post<any>(environment.coursesApiUrl + '/api/assessments', body).pipe(
      tap(() => {
        this.getCreatedSubscription(courseId).next();
      }),
      finalize(() => {
        const next = new Set(creating$.value);
        next.delete(tempId);
        creating$.next(next);
      })
    );
  };

  public getAssessmentsExportDataByCourseId(courseId: string, limit: number = 10) {
    const url = environment.coursesApiUrl + `/api/assessments/export/courses/${courseId}`;
    window.open(url);
  }

  public getAssessmentExportDataBySessionId(courseId: string, sessionId: string, format: string = 'pdf'): Observable<boolean> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = environment.coursesApiUrl + `/api/assessments/export/courses/${courseId}/sessions/${sessionId}?format=${format}&timeZone=${timeZone}`;

    return this._http.get(url, { responseType: 'blob' }).pipe(
      tap(response => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `assessments-export_course_${courseId}-session_${sessionId}__${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        document.removeChild(link);
      }),
      map(() => true),
      catchError(error => {
        return of(false);
      })
    );
  }

  private getCreatingSubscription(courseId: string) {
    if (!this.creatingByCourse.has(courseId)) {
      this.creatingByCourse.set(courseId, new BehaviorSubject<Set<string>>(new Set()));
    }
    return this.creatingByCourse.get(courseId)!;
  }

  private getCreatedSubscription(courseId: string) {
    if (!this.createdByCourse.has(courseId)) {
      this.createdByCourse.set(courseId, new Subject<void>());
    }
    return this.createdByCourse.get(courseId)!;
  }
}
